// SearchModal — central search för vidalv.live (Build 92).
// Hämtar från /api/search som returnerar grupp-info per typ.
import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Search, X, MapPin, Calendar, Users, Briefcase, Map as MapIcon, ShoppingBag, FileText } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_META = {
  plats:        { label: "Platser",     icon: MapPin,     color: "text-falu",   emoji: "📍" },
  foretag:      { label: "Företag",     icon: Briefcase,  color: "text-pine",   emoji: "🏢" },
  forening:     { label: "Föreningar",  icon: Users,      color: "text-pine",   emoji: "🤝" },
  evenemang:    { label: "Evenemang",   icon: Calendar,   color: "text-falu",   emoji: "📅" },
  karta:        { label: "Kartor",      icon: MapIcon,    color: "text-bark",   emoji: "🗺" },
  vardagskort:  { label: "Vardagskort", icon: ShoppingBag, color: "text-bark",  emoji: "🛒" },
  // Backwards-compatible legacy types (in case something still emits them)
  anslag:       { label: "Anslag",      icon: FileText,   color: "text-pine",   emoji: "📌" },
  "förening":   { label: "Föreningar",  icon: Users,      color: "text-pine",   emoji: "🤝" },
  praktiskt:    { label: "Praktiskt",   icon: MapPin,     color: "text-bark",   emoji: "📍" },
  "tjänst":     { label: "Företag",     icon: Briefcase,  color: "text-pine",   emoji: "🏢" },
};

export default function SearchModal({ open, onClose }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (open) {
      setQuery("");
      setResults([]);
      setGroups([]);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [open]);

  useEffect(() => {
    if (query.length < 2) { setResults([]); setGroups([]); return; }
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/search`, { params: { q: query } });
        setResults(res.data.results || []);
        setGroups(res.data.groups || []);
      } catch {
        setResults([]);
        setGroups([]);
      }
      setLoading(false);
    }, 250);
    return () => clearTimeout(timer);
  }, [query]);

  const handleSelect = (url) => {
    onClose();
    navigate(url);
  };

  if (!open) return null;

  // Group results for display
  const byTyp = {};
  for (const r of results) {
    if (!byTyp[r.typ]) byTyp[r.typ] = [];
    byTyp[r.typ].push(r);
  }
  const orderedTypes = groups.map(g => g.typ);

  return (
    <div data-testid="search-modal" className="fixed inset-0 z-[100] flex items-start justify-center pt-16 sm:pt-24 px-4">
      <div className="fixed inset-0 bg-ink/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-xl bg-parchment border border-warm-border rounded-sm shadow-xl overflow-hidden">
        <div className="flex items-center gap-3 px-4 py-3 border-b border-warm-border">
          <Search size={18} className="text-bark shrink-0" />
          <input
            ref={inputRef}
            data-testid="search-input"
            type="text"
            placeholder="Sök i hela bygden..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="flex-1 text-sm text-ink bg-transparent outline-none placeholder:text-bark/50 font-body"
          />
          <button onClick={onClose} data-testid="search-close" className="p-1 text-bark hover:text-ink transition-colors">
            <X size={18} />
          </button>
        </div>

        <div className="max-h-[65vh] overflow-y-auto">
          {loading && <p className="px-4 py-6 text-sm text-bark text-center">Söker...</p>}

          {!loading && query.length >= 2 && results.length === 0 && (
            <p className="px-4 py-6 text-sm text-bark text-center">Inga resultat för &quot;{query}&quot;</p>
          )}

          {!loading && results.length > 0 && (
            <div className="py-2">
              {orderedTypes.map((typ) => {
                const meta = TYPE_META[typ] || TYPE_META.praktiskt;
                const items = byTyp[typ] || [];
                if (items.length === 0) return null;
                return (
                  <div key={typ} className="mb-2" data-testid={`search-group-${typ}`}>
                    <div className="px-4 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-bark/70 bg-bone-alt/40">
                      <span className="mr-1.5">{meta.emoji}</span>{meta.label}
                      <span className="text-bark/45 font-normal normal-case ml-1">· {items.length}</span>
                    </div>
                    <ul>
                      {items.map((r) => {
                        const Icon = meta.icon;
                        return (
                          <li key={`${r.typ}-${r.id}`}>
                            <button
                              data-testid={`search-result-${r.id}`}
                              onClick={() => handleSelect(r.url)}
                              className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-bone-alt/60 text-left transition-colors"
                            >
                              <Icon size={15} className={`${meta.color} shrink-0`} />
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-ink truncate">{r.titel}</p>
                                {r.sub && <p className="text-xs text-bark/65 truncate">{r.sub}</p>}
                              </div>
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                );
              })}
            </div>
          )}

          {!loading && query.length < 2 && (
            <p className="px-4 py-6 text-sm text-bark text-center">
              Skriv minst 2 tecken — sök i platser, företag, föreningar, evenemang, kartor och vardagskort
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
